"use client";

import { useEffect, useRef } from "react";
import {
  loadSortCriteriaActions,
  SortBy,
  SortOrder,
  type SearchEngine,
  buildSearchStatus,
} from "@coveo/headless";
import { type CatalogTaxonomySelection } from "./search-filter.props";
import {
  applyFacetPreselect,
  FacetSelection,
} from "lib/search/preselectFacets";

interface CatalogDatasource {
  applications?: { targetItems: CatalogTaxonomySelection[] };
  solutions?: { targetItems: CatalogTaxonomySelection[] };
  industries?: { targetItems: CatalogTaxonomySelection[] };
  markets?: { targetItems: CatalogTaxonomySelection[] };
}

export interface UseCatalogFilterProps {
  engine: SearchEngine | undefined;
  catalogId: string | undefined;
  selections: FacetSelection[];
}

// Shared client-side behaviour for the OfferingCatalog and SolutionCatalog
// variants:
//   1. Register an alphabetical-by-page-title sort on the Coveo engine once.
//   2. Mirror the catalog's selected applications/solutions into the URL hash
//      so the page is shareable and the Atomic facet UI stays in sync.
export const useCatalogFilter = (
  searchEngine: SearchEngine | undefined,
  datasource: CatalogDatasource | undefined,
  catalogId: string | undefined,
  selections: FacetSelection[],
) => {
  const appliedForCatalogIdRef = useRef<string | null>(null);
  const selectionsRef = useRef<FacetSelection[]>(selections);
  const sortAppliedRef = useRef<boolean>(false);
  // eslint-disable-next-line react-hooks/refs
  selectionsRef.current = selections;

  useEffect(() => {
    if (sortAppliedRef.current) return;
    if (!searchEngine) return;
    if (!catalogId) return;
    if (appliedForCatalogIdRef.current === catalogId) return;

    const sortCreators = loadSortCriteriaActions(searchEngine);
    searchEngine.dispatch(
      sortCreators.registerSortCriterion([
        {
          by: SortBy.Field,
          order: SortOrder.Ascending,
          field: "offering_type_order",
        },
        {
          by: SortBy.Field,
          order: SortOrder.Ascending,
          field: "page_og_title",
        },
      ]),
    );
    sortAppliedRef.current = true;
  }, [searchEngine]);

  useEffect(() => {
    if (!searchEngine || !catalogId) return;
    if (appliedForCatalogIdRef.current === catalogId) return;

    const status = buildSearchStatus(searchEngine);

    let applied = false;
    let unsubscribe: (() => void) | null = null;

    const tryApply = () => {
      if (applied) return;
      if (!status.state.firstSearchExecuted || status.state.isLoading) return;

      const currentSelections = selectionsRef.current;
      const hasAnyValue = currentSelections.some((s) => s.values.length > 0);
      if (!hasAnyValue) return;

      applied = true;
      appliedForCatalogIdRef.current = catalogId;
      applyFacetPreselect(searchEngine, currentSelections);
      unsubscribe?.();
      unsubscribe = null;
    };

    // First search may already be done by the time this effect runs.
    tryApply();
    if (!applied) {
      unsubscribe = searchEngine.subscribe(tryApply);
    }

    return () => {
      unsubscribe?.();
    };
  }, [searchEngine, catalogId, selections]);
};
